import type { ImapSettings, SmtpSettings, IChannelState } from 'src/types/checkout';

import { toast } from 'sonner';
import { isEqual } from 'es-toolkit';
import { useLocalStorage } from 'minimal-shared/hooks';
import { useMemo, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter, usePathname, useSearchParams } from 'src/routes/hooks';

import { useTranslate } from 'src/locales';
import { emailStep1Data, channelStep1Data } from 'src/_mock';

import { ChannelContext } from './channel-context';
import { useCreateChannelMutation } from '../slice/channelApi';

// ----------------------------------------------------------------------

const CHECKOUT_STORAGE_KEY = 'app-channel';
const CHANNEL_STEPS = (t: any) => [
  {
    header: t('CHOOSE_PROVIDER'),
    description: t('CHOOSE_PROVIDER_DESCRIPTION'),
  },
  {
    header: t('CREATE_CHANNEL'),
    description: t('CREATE_CHANNEL_DESCRIPTION'),
  },
  {
    header: t('ADD_USERS'),
    description: t('ADD_USERS_DESCRIPTION'),
  },
  {
    header: t('FINISH'),
    description: t('FINISH_DESCRIPTION'),
  },
];

const initialState: IChannelState = {
  channelType: '',
  channelName: '',
  whatsappPin: '',
  assignedType: '',
  assignedId: { id: '', label: '' },
  facebookDetails: {
    main_token: '',
  },
  // EMAIL
  emailProviderType: '',
  emailChannelDtls: { channelName: '', email: '' },
  googleEmailDetails: { code: '' },
  microsoftEmailDetails: { code: '' },
  LiveChatDeatil: {
    websiteDomin: '',
    websiteName: '',
    widgetColor: '',
    welcomeTitle: '',
    welcomeTagline: '',
  },
  smtpDetails: {
    smtpEnabled: true,
    smtpAddress: '',
    smtpPort: 0,
    smtpLogin: '',
    smtpPassword: '',
    smtpDomain: '',
  },
  imapDetails: {
    imapEnabled: true,
    imapAddress: '',
    imapPort: 0,
    imapLogin: '',
    imapPassword: '',
    imapEnableSsl: true,
  },
  voiceCallDetails: {
    assignedId: null,
    DidType: [],
    assignedType: '',
  },
  channelCreatedResponse: null,
};

// ----------------------------------------------------------------------

type CheckoutProviderProps = {
  children: React.ReactNode;
};

export function ChannelProvider({ children }: CheckoutProviderProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { t } = useTranslate('translation');

  const activeStep = pathname.includes('') ? Number(searchParams.get('step')) : null;

  const isUserInStep = pathname.includes('step') || pathname.includes('edit');

  const [createChannel, { isLoading }] = useCreateChannelMutation();

  const { state, setState, setField } = useLocalStorage<IChannelState>(
    CHECKOUT_STORAGE_KEY,
    initialState,
    { initializeWithValue: false }
  );
  const canReset = !isEqual(state, initialState);
  const completed = activeStep === CHANNEL_STEPS.length;
  const handleResetState = useCallback(() => {
    setField('channelType', '');
    setField('channelName', '');
    setField('whatsappPin', '');
    setField('assignedId', { id: '', label: '' });
    setField('assignedType', '');
    setField('facebookDetails', {
      main_token: '',
    });
    // EMAIL
    setField('emailProviderType', '');
    setField('emailChannelDtls', { channelName: '', email: '' });
    setField('googleEmailDetails', { code: '' });
    setField('microsoftEmailDetails', { code: '' });
    setField('smtpDetails', {
      smtpEnabled: true,
      smtpAddress: '',
      smtpPort: 0,
      smtpLogin: '',
      smtpPassword: '',
      smtpDomain: '',
    });
    setField('imapDetails', {
      imapEnabled: true,
      imapAddress: '',
      imapPort: 0,
      imapLogin: '',
      imapPassword: '',
      imapEnableSsl: true,
    });
    // live Chat
    setField('LiveChatDeatil', {
      websiteDomin: '',
      websiteName: '',
      widgetColor: '',
      welcomeTitle: '',
      welcomeTagline: '',
    });
    setField('channelCreatedResponse', null);
    // voice call
    setField('voiceCallDetails', {
      assignedId: null,
      DidType: '',
      assignedType: '',
    });
  }, [setField]);

  const createChannelFunc = useCallback(
    (formData: any) =>
      createChannel(formData)
        .unwrap()
        .then((res: any) => {
          setField('channelCreatedResponse', res.data);
          toast.success(t('CHANNEL_CREATED_SUCCESSFULLY'));
          return true;
        })
        .catch((err: any) => {
          const errorMessage = err?.data?.message;

          const errorMap: Record<string, string> = {
            'channelName already exists': 'DUPLICATE_CHANNEL',
            'phoneNumber already exists': 'DUPLICATE_NUMBER',
            'No matching DID available in stock for any eligible subscription.':
              'NO_MATCHING_DID_IN_STOCK',
            'No available DIDs to assign: All tags in your current packages are already used. Please add new AddOn for that.':
              'NO_AVAILABLE_DIDS_TO_ASSIGN',
            'You have already created a DID number based on your current package. Please extend your package limit to add more.':
              'DID_LIMIT_REACHED',
          };

          if (errorMessage && errorMap[errorMessage]) {
            toast.error(t(errorMap[errorMessage]));
            return;
          }

          toast.error(errorMessage || 'Something went wrong');
        }),
    []
  );

  const onChangeStep = useCallback(
    async (type: 'back' | 'next' | 'go', step?: number) => {
      const stepNumbers = {
        back: (activeStep ?? 0) - 1,
        next: (activeStep ?? 0) + 1,
        go: step ?? 0,
      };

      const targetStep = stepNumbers[type];

      // remove the selectedchannel
      if (targetStep === -1) handleResetState();
      if (targetStep === 0) {
        setField('channelName', '');
        setField('emailProviderType', '');
      }
      if (targetStep === 1) {
        setField('assignedId', { id: '', label: '' });
        setField('emailChannelDtls', { channelName: '', email: '' });
        setField('googleEmailDetails', { code: '' });
        setField('microsoftEmailDetails', { code: '' });
        if (state.channelType === 'VOICE') {
          setField('voiceCallDetails', {
            assignedId: null,
            DidType: '',
            assignedType: '',
          });
        }
      }

      if (targetStep === 2) {
        if (state.channelType === 'EMAIL' && state.emailProviderType === 'other_provider') {
          setField('smtpDetails', {
            smtpEnabled: true,
            smtpAddress: '',
            smtpPort: 0,
            smtpLogin: '',
            smtpPassword: '',
            smtpDomain: '',
          });
        }
      }

      if (targetStep === 3) {
        let updateFormdata = null;

        if (state.channelType === 'WHATSAPP') {
          updateFormdata = {
            channelName: state.channelName,
            channelType: state.channelType,
            pin: state.whatsappPin,
            code: state.facebookDetails.main_token,
            assignedType: state.assignedType.toUpperCase() || '',
            assignedId: state.assignedId?.id || '',
          };
        }

        if (state.channelType === 'LIVE_CHAT') {
          updateFormdata = {
            channelName: state.LiveChatDeatil.websiteName,
            channelType: state.channelType,
            domainUrl: state.LiveChatDeatil.websiteDomin,
            welcomeTagline: state.LiveChatDeatil.welcomeTagline,
            welcomeTitle: state.LiveChatDeatil.welcomeTitle,
            widgetColor: state.LiveChatDeatil.widgetColor,
            assignedType: state.assignedType.toUpperCase() || '',
            assignedId: state.assignedId?.id || '',
          };
        }

        if (state.channelType === 'VOICE') {
          updateFormdata = {
            channelName: state.channelName,
            channelType: 'VOICE',
            assignedType: state.voiceCallDetails.assignedType.toUpperCase() || '',
            assignedId: state.voiceCallDetails.assignedId.id || '',
            didTags: state.voiceCallDetails.DidType,
          };
        }
        if (updateFormdata) {
          const success = await createChannelFunc(updateFormdata);

          if (!success) return; // ✅ STOP IF FAILED
        }

        // This block runs for both WHATSAPP and LIVE_CHAT if email conditions also match
        if (state.channelType === 'EMAIL' && state.emailProviderType === 'other_provider') {
          setField('imapDetails', {
            imapEnabled: true,
            imapAddress: '',
            imapPort: 0,
            imapLogin: '',
            imapPassword: '',
            imapEnableSsl: true,
          });
        }
      }

      if (targetStep === 4) {
        if (
          state.channelType === 'WHATSAPP' ||
          state.channelType === 'VOICE' ||
          state.channelType === 'LIVE_CHAT'
        ) {
          router.replace(paths.channel.root);
          handleResetState();
          return;
        }

        if (state.channelType === 'EMAIL' && state.emailProviderType !== 'other_provider') {
          const { emailProviderType, googleEmailDetails, microsoftEmailDetails } = state;
          const code =
            emailProviderType === 'google' ? googleEmailDetails.code : microsoftEmailDetails.code;

          const updateFormdata = {
            channelName: state.emailChannelDtls.channelName,
            provider: emailProviderType,
            channelType: state.channelType,
            assignedType: state.assignedType.toUpperCase(),
            assignedId: state.assignedId.id,
            code,
          };

          const success = await createChannelFunc(updateFormdata);
          if (!success) return; // ✅ STOP IF FAILED
        }

        if (state.channelType === 'EMAIL' && state.emailProviderType === 'other_provider') {
          setField('assignedType', '');
          setField('assignedId', { id: '', label: '' });
        }
      }

      if (targetStep === 5) {
        if (state.channelType === 'EMAIL' && state.emailProviderType !== 'other_provider') {
          router.replace(paths.channel.root);
          handleResetState();
          return;
        }
      }

      if (targetStep === 6) {
        if (state.channelType === 'EMAIL' && state.emailProviderType === 'other_provider') {
          const { smtpDetails, imapDetails } = state;

          const { smtpEnabled, smtpAddress, smtpPort, smtpLogin, smtpPassword, smtpDomain } =
            smtpDetails;
          const { imapEnabled, imapAddress, imapPort, imapLogin, imapPassword, imapEnableSsl } =
            imapDetails;

          const updateFormdata = {
            channelName: state.emailChannelDtls.channelName,
            email: state.emailChannelDtls.email,
            channelType: state.channelType,
            assignedType: state.assignedType.toUpperCase(),
            assignedId: state.assignedId.id,
            provider: 'other',
            smtpEnabled,
            smtpAddress,
            smtpPort,
            smtpLogin,
            smtpPassword,
            smtpDomain,
            imapEnabled,
            imapAddress,
            imapPort,
            imapLogin,
            imapPassword,
            imapEnableSsl,
          };

          const success = await createChannelFunc(updateFormdata);
          if (!success) return; // ✅ STOP IF FAILED
        }
      }

      if (targetStep === 7) {
        if (state.channelType === 'EMAIL' && state.emailProviderType === 'other_provider') {
          router.replace(paths.channel.root);
          handleResetState();
          return;
        }
      }

      const queryString = new URLSearchParams({ step: `${targetStep}` }).toString();

      const redirectPath =
        targetStep === 0 ? paths.channel.step : `${paths.channel.step}?${queryString}`;

      router.push(redirectPath);
    },
    [activeStep, createChannelFunc, handleResetState, router, setField, state]
  );

  const onSelectChannel = useCallback(
    (value: string) => {
      setField('channelType', value);
    },
    [setField]
  );

  const onSelectEmailProvider = useCallback(
    (value: string) => {
      setField('emailProviderType', value);
    },
    [setField]
  );

  const handleAddUser = useCallback(
    (value: { id: string; label: string }) => {
      setField('assignedId', value);
    },
    [setField]
  );
  const handleAddUserType = useCallback(
    (value: string) => {
      setField('assignedType', value);
    },
    [setField]
  );

  const handleChannelName = useCallback(
    (value: string) => {
      setField('channelName', value);
    },
    [setField]
  );
  const handleWhatsappPin = useCallback(
    (value: string) => {
      setField('whatsappPin', value);
    },
    [setField]
  );

  const handleEmailChannelDtls = useCallback(
    (value: { channelName: string; email: string }) => {
      setField('emailChannelDtls', value);
    },
    [setField]
  );

  const handleFaceBookDeteail = useCallback(
    (value: any) => {
      setField('facebookDetails', value);
    },
    [setField]
  );

  const handleGoogleDetail = useCallback(
    (value: string) => {
      setField('googleEmailDetails', { code: value });
    },
    [setField]
  );

  const handleLiveChatChannelDtls = useCallback(
    (value: {
      websiteName: string;
      websiteDomin: string;
      widgetColor: string;
      welcomeTitle: string;
      welcomeTagline: string;
    }) => {
      setField('LiveChatDeatil', value);
    },
    [setField]
  );

  const handleMicrosoftDetail = useCallback(
    (value: string) => {
      setField('microsoftEmailDetails', { code: value });
    },
    [setField]
  );

  const handleStepHeader = useCallback(() => {
    const isEmailChannel = state.channelType === 'EMAIL';

    let updatedActiveStep;
    if (isEmailChannel) {
      // on activeStep(1) => consider => 0 otherwise activeStep(- 1)
      updatedActiveStep = [0, 1].includes(activeStep ?? 0) ? 0 : (activeStep ?? 0) - 1;
    } else {
      updatedActiveStep = activeStep;
    }

    switch (updatedActiveStep) {
      case 0:
        return t('CHOOSE_A_PROVIDER');
      case 1: {
        const activeList = isEmailChannel ? emailStep1Data(t) : channelStep1Data(t);
        const activeType = isEmailChannel ? state.emailProviderType : state.channelType;

        const selectedChannelData = activeList.find((data) => data.value === activeType);
        return selectedChannelData
          ? `${selectedChannelData?.channel_name} ${isEmailChannel ? t('EMAIL_CHANNEL') : t('CHANNEL')}`
          : `${t('UNKNOWN')} ${t('CHANNEL')}`;
      }
      case 2:
        return state.emailProviderType === 'other_provider'
          ? t('SMTP_CONFIGURATION')
          : t('ADD_USER_AND_TEAMS');

      case 3:
        return state.emailProviderType === 'other_provider' ? t('IMAP_CONFIGURATION') : '';
      case 4:
        return state.emailProviderType === 'other_provider' ? t('ADD_USER_AND_TEAMS') : '';
      case 5:
        return state.emailProviderType === 'other_provider' ? t('FINISH') : '';
      default:
        return t('STEP');
    }
  }, [activeStep, state.channelType, state.emailProviderType]);

  const handleSMTPDetails = useCallback(
    (value: SmtpSettings) => {
      setField('smtpDetails', value);
    },
    [setField]
  );

  const handleOnChannelCreated = useCallback(
    (value: any) => {
      setField('channelCreatedResponse', value);
    },
    [setField]
  );

  const handleIMAPDetails = useCallback(
    (value: ImapSettings) => {
      setField('imapDetails', value);
    },
    [setField]
  );

  const memoizedValue = useMemo(
    () => ({
      state,
      setState,
      setField,
      /********/
      activeStep,
      onChangeStep,
      steps: CHANNEL_STEPS,
      /********/
      canReset,
      completed,
      /********/
      onSelectChannel,
      handleStepHeader,
      handleAddUser,
      handleAddUserType,
      handleChannelName,
      handleWhatsappPin,
      handleFaceBookDeteail,
      // EMAIL (IN-PROGRESS)
      onSelectEmailProvider,
      handleEmailChannelDtls,
      handleGoogleDetail,
      handleMicrosoftDetail,
      handleLiveChatChannelDtls,
      handleOnChannelCreated,
      handleSMTPDetails,
      handleIMAPDetails,
      isLoading,
    }),
    [
      state,
      setState,
      setField,
      activeStep,
      onChangeStep,
      canReset,
      completed,
      onSelectChannel,
      handleStepHeader,
      handleAddUser,
      handleAddUserType,
      handleChannelName,
      handleWhatsappPin,
      handleFaceBookDeteail,
      // EMAIL (IN-PROGRESS)
      onSelectEmailProvider,
      handleEmailChannelDtls,
      handleGoogleDetail,
      handleMicrosoftDetail,
      handleLiveChatChannelDtls,
      handleOnChannelCreated,
      handleSMTPDetails,
      handleIMAPDetails,
      isLoading,
    ]
  );

  // RESET STATE ON PAGE CHANGE
  useEffect(() => {
    if (!isUserInStep) handleResetState();
  }, [isUserInStep, handleResetState]);

  return <ChannelContext.Provider value={memoizedValue}>{children}</ChannelContext.Provider>;
}
